import { Route, Navigate, Routes, BrowserRouter } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';

import 'react-toastify/dist/ReactToastify.css';

// Create Sentry-wrapped Routes component for better error tracking
const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

import SidebarMenu from '@components/SidebarMenu';
import NotFound from '@screens/NotFound';
import InterviewSelector from '@screens/InterviewSelector';
import UsersManager from '@screens/UsersManager';
import Results from '@screens/Results';
import DevicesTable from '@screens/DevicesTable';
import ErrorBoundary from '@components/ErrorBoundary';
import MobileAppLink from '@screens/MobileAppLink';
import InterviewEditor from '@screens/InterviewEditor';
import AcknowledgementsScreen from '@screens/AcknowledgementsScreen';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import RequestAccessScreen from '@screens/RequestAccessScreen';
import { useIsAccessAdmin, useAuthUser } from './FirebaseAuthentication';
import AccessRequestsScreen from '@screens/AccessRequestsScreen';
import AccessPIN from '@screens/AccessPIN';
import LoginPage from '@components/LoginPage';

export default function AppRoutes() {
    const { selectedInterview, loading } = useInterviewOptions();
    const isAccessAdmin = useIsAccessAdmin();
    const authUser = useAuthUser();

    return (
        <>
            <ToastContainer />
            <BrowserRouter>
                <SentryRoutes>
                    {/* Public Routes */}
                    <Route path="/" element={<Navigate to="/login" replace={true} />} />
                    <Route
                        path="/login"
                        element={
                            <ErrorBoundary location={`Route - LoginPage`}>
                                <LoginPage />
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/request-access"
                        element={
                            <ErrorBoundary location={`Route - request-access`}>
                                <RequestAccessScreen />
                            </ErrorBoundary>
                        }
                    />
                    <Route
                        path="/acknowledgements"
                        element={
                            <ErrorBoundary location={`Route - acknowledgements`}>
                                <AcknowledgementsScreen />
                            </ErrorBoundary>
                        }
                    />

                    {/* Protected Routes - require authentication */}
                    {authUser ? (
                        <Route
                            path="/app"
                            element={
                                <ErrorBoundary location={`Route - SidebarMenu`}>
                                    <SidebarMenu visible={!!selectedInterview} />
                                </ErrorBoundary>
                            }
                        >
                            <Route
                                index
                                element={
                                    <Navigate
                                        to={
                                            selectedInterview ? '/app/survey' : '/app/select-survey'
                                        }
                                        replace={true}
                                    />
                                }
                            />
                            <Route
                                path="select-survey"
                                element={
                                    <ErrorBoundary location={`Route - InterviewSelector`}>
                                        <InterviewSelector />
                                    </ErrorBoundary>
                                }
                            />
                            <Route
                                path="acknowledgements"
                                element={
                                    <ErrorBoundary location={`Route - acknowledgements (auth)`}>
                                        <AcknowledgementsScreen />
                                    </ErrorBoundary>
                                }
                            />

                            {/* ACCESS ADMINS */}
                            {isAccessAdmin && (
                                <Route
                                    path="access-requests"
                                    element={
                                        <ErrorBoundary location={`Route - Access Requests`}>
                                            <AccessRequestsScreen />
                                        </ErrorBoundary>
                                    }
                                />
                            )}

                            {/* PROTECTED ROUTES REQUIRING SELECTED INTERVIEW */}
                            {selectedInterview && (
                                <>
                                    <Route
                                        path="survey"
                                        element={
                                            <ErrorBoundary location={`Route - InterviewEditor`}>
                                                <InterviewEditor />
                                            </ErrorBoundary>
                                        }
                                    />
                                    <Route
                                        path="access-manager"
                                        element={
                                            <ErrorBoundary location={`Route - UsersManager`}>
                                                <UsersManager />
                                            </ErrorBoundary>
                                        }
                                    />
                                    <Route
                                        path="devices"
                                        element={
                                            <ErrorBoundary location={`Route - DevicesTable`}>
                                                <DevicesTable />
                                            </ErrorBoundary>
                                        }
                                    />
                                    <Route
                                        path="results"
                                        element={
                                            <ErrorBoundary location={`Route - Results`}>
                                                <Results />
                                            </ErrorBoundary>
                                        }
                                    />
                                    <Route
                                        path="access-pin"
                                        element={
                                            <ErrorBoundary location={`Route - access pin`}>
                                                <AccessPIN />
                                            </ErrorBoundary>
                                        }
                                    />
                                    <Route
                                        path="mobile-app"
                                        element={
                                            <ErrorBoundary location={`Route - MobileAppLink`}>
                                                <MobileAppLink />
                                            </ErrorBoundary>
                                        }
                                    />
                                </>
                            )}
                        </Route>
                    ) : (
                        /* Redirect unauthenticated users trying to access protected routes */
                        <Route path="/app/*" element={<Navigate to="/login" replace={true} />} />
                    )}

                    {/* Redirect legacy routes to new structure */}
                    <Route
                        path="/select-survey"
                        element={<Navigate to="/app/select-survey" replace={true} />}
                    />
                    <Route path="/survey" element={<Navigate to="/app/survey" replace={true} />} />

                    {/* Catch all */}
                    <Route
                        path="*"
                        element={
                            <ErrorBoundary location={`Route - NotFound`}>
                                <NotFound loading={loading} />
                            </ErrorBoundary>
                        }
                    />
                </SentryRoutes>
            </BrowserRouter>
        </>
    );
}
